import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { clsx } from "clsx";
import {
  LayoutDashboard,
  Server,
  Container,
  PlusCircle,
  Archive,
  Activity,
  Receipt,
  Network,
  Share2,
  Scale,
  ShieldCheck,
  LineChart,
  Settings,
  LogOut,
  BookOpen,
  Sparkles,
  Star,
  Users as UsersIcon,
  Building2,
  FileClock,
  ShieldAlert,
  GitCompareArrows,
} from "lucide-react";
import { TenantSelector } from "@/components/tenant/TenantSelector";
import { useAuth } from "@/auth/useAuth";

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navigation: NavItem[] = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard },
  { name: "Quick start", href: "/onboarding", icon: Sparkles },
  { name: "Virtual Machines", href: "/vms", icon: Server },
  { name: "Docker", href: "/docker", icon: Container },
  { name: "Provisioning", href: "/provisioning", icon: PlusCircle },
  { name: "Backups", href: "/backups", icon: Archive },
  { name: "Monitoring", href: "/monitoring", icon: Activity },
  { name: "Billing", href: "/billing", icon: Receipt },
  { name: "IPAM", href: "/ipam", icon: Network },
  { name: "Knowledge", href: "/knowledge", icon: BookOpen },
  { name: "Topology", href: "/topology", icon: Share2 },
  { name: "Load balancer", href: "/lb", icon: Scale },
  { name: "Ops", href: "/ops", icon: ShieldCheck },
  { name: "Tenants", href: "/admin/tenants", icon: Building2 },
  { name: "Utilisateurs", href: "/admin/users", icon: UsersIcon },
  { name: "Audit log", href: "/admin/audit", icon: FileClock },
  { name: "Vault", href: "/admin/vault", icon: ShieldAlert },
  { name: "Reconciliation", href: "/admin/reconciliation", icon: GitCompareArrows },
  {
    name: "Grafana dashboards",
    href: "/admin/monitoring/dashboards",
    icon: LineChart,
  },
  { name: "Settings", href: "/settings", icon: Settings },
];

// Per-user favorites — keyed by Keycloak sub so each operator's pinned
// nav items survive reloads without polluting other users' layouts.
function useFavorites(userSub: string | undefined): {
  favs: Set<string>;
  toggle: (href: string) => void;
} {
  const key = userSub ? `alphaprox.sidebar.favs.${userSub}` : null;
  const [favs, setFavs] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!key) {
      setFavs(new Set());
      return;
    }
    try {
      const raw = localStorage.getItem(key);
      if (raw) setFavs(new Set(JSON.parse(raw) as string[]));
    } catch {
      // Corrupted localStorage — ignore; next toggle rewrites it.
    }
  }, [key]);

  const toggle = (href: string) => {
    setFavs((prev) => {
      const next = new Set(prev);
      if (next.has(href)) next.delete(href);
      else next.add(href);
      if (key) {
        try {
          localStorage.setItem(key, JSON.stringify(Array.from(next)));
        } catch {
          // localStorage quota hit — silently drop; state still works in-memory.
        }
      }
      return next;
    });
  };

  return { favs, toggle };
}

function NavRow({
  item,
  pinned,
  onTogglePin,
}: {
  item: NavItem;
  pinned: boolean;
  onTogglePin: () => void;
}) {
  return (
    <li className="group relative">
      <NavLink
        to={item.href}
        end={item.href === "/"}
        className={({ isActive }) =>
          clsx(
            "flex items-center gap-3 rounded-lg px-3 py-2.5 pr-8 text-sm font-medium transition-colors",
            isActive
              ? "bg-blue-600/20 text-blue-400"
              : "text-gray-400 hover:bg-gray-800 hover:text-gray-200",
          )
        }
      >
        <item.icon className="h-5 w-5 flex-shrink-0" />
        {item.name}
      </NavLink>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onTogglePin();
        }}
        title={pinned ? "Retirer des favoris" : "Ajouter aux favoris"}
        className={clsx(
          "absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 transition-opacity",
          pinned
            ? "text-amber-400 opacity-100"
            : "text-gray-600 opacity-0 hover:text-amber-400 group-hover:opacity-100",
        )}
      >
        <Star className="h-3.5 w-3.5" fill={pinned ? "currentColor" : "none"} />
      </button>
    </li>
  );
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const { favs, toggle } = useFavorites(user?.id);

  const favItems = navigation.filter((i) => favs.has(i.href));
  const hasFavs = favItems.length > 0;

  return (
    <aside className="flex h-screen w-64 flex-col border-r border-gray-800 bg-gray-900">
      {/* Logo */}
      <div className="flex h-16 items-center gap-2 border-b border-gray-800 px-6">
        <Server className="h-7 w-7 text-blue-500" />
        <span className="text-xl font-bold text-gray-100">AlphaProx</span>
      </div>

      {/* Tenant Selector */}
      <div className="border-b border-gray-800 px-4 py-3">
        <TenantSelector />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {hasFavs && (
          <>
            <div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-gray-600">
              Favoris
            </div>
            <ul className="mb-4 space-y-1">
              {favItems.map((item) => (
                <NavRow
                  key={`fav-${item.href}`}
                  item={item}
                  pinned
                  onTogglePin={() => toggle(item.href)}
                />
              ))}
            </ul>
            <div className="mb-2 mt-2 border-t border-gray-800" />
            <div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-gray-600">
              Tous
            </div>
          </>
        )}
        <ul className="space-y-1">
          {navigation.map((item) => (
            <NavRow
              key={item.href}
              item={item}
              pinned={favs.has(item.href)}
              onTogglePin={() => toggle(item.href)}
            />
          ))}
        </ul>
      </nav>

      {/* User Info */}
      <div className="border-t border-gray-800 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-sm font-medium text-white">
            {user?.firstName?.charAt(0) ?? ""}
            {user?.lastName?.charAt(0) ?? ""}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-200">
              {user?.fullName ?? "User"}
            </p>
            <p className="truncate text-xs text-gray-500">
              {user?.email ?? ""}
            </p>
          </div>
          <button
            onClick={logout}
            title="Sign out"
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-800 hover:text-gray-300"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
